import { useEffect, useRef } from "react";
import styles from "./DeviceFrames.module.css";
import type { ScreenshotTemplate } from "./ScreenshotTemplates.tsx";
import type { DeviceSpec } from "./devices.ts";
import { renderScreenshot } from "./renderScreenshot.ts";

interface DeviceFrameProps {
  device: DeviceSpec;
  template: ScreenshotTemplate;
  headline: string;
  subtitle: string;
  screenshotImage: HTMLImageElement | null;
  maxPreviewHeight?: number;
}

export function DeviceFrame({
  device,
  template,
  headline,
  subtitle,
  screenshotImage,
  maxPreviewHeight = 640,
}: DeviceFrameProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Calculate preview scale to fit within container
  const previewScale = Math.min(maxPreviewHeight / device.height, 400 / device.width, 1);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    renderScreenshot(canvas, {
      device,
      template,
      headline,
      subtitle,
      screenshotImage,
      scale: previewScale,
    });
  }, [device, template, headline, subtitle, screenshotImage, previewScale]);

  return (
    <div className={styles.frameContainer}>
      <canvas
        ref={canvasRef}
        className={styles.canvas}
        style={{
          width: device.width * previewScale,
          height: device.height * previewScale,
        }}
      />
      <div className={styles.deviceLabel}>
        <span className={styles.deviceName}>{device.name}</span>
        <span className={styles.deviceSize}>
          {device.width} x {device.height}
        </span>
        {device.required && <span className={styles.requiredBadge}>Required</span>}
      </div>
    </div>
  );
}
